import { useRef, useState } from "react";
import { t } from "i18next";
import { ICON_BASE_PATH } from "../../../../utils/config";

interface LicenseUploadChangeEvent {
  value: File | null;
}

interface FormLicenseUploadProps {
  onChange: (event: LicenseUploadChangeEvent) => void;
  value?: File | null;
  validationMessage?: string;
  touched?: boolean;
  accept?: string;
  label: string;
}

const LicenseUpload: React.FC<FormLicenseUploadProps> = ({
  onChange,
  label,
  validationMessage,
  touched,
  accept,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [logo, setLogo] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [isImage, setIsImage] = useState<boolean>(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      onChange({ value: file });
      setFileName(file.name);
      setIsImage(file.type.startsWith("image/"));
      const previewURL = URL.createObjectURL(file);
      setLogo(previewURL);
    }
  };

  const handleOnClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (logo) {
      URL.revokeObjectURL(logo);
    }
    setLogo("");
    setFileName("");
    setIsImage(false);
    onChange({ value: null });

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  return (
    <div
      className="w-full h-30 border-2 border-dashed border-[#D9D9D9] rounded-xl flex flex-col justify-center items-center text-center cursor-pointer hover:border-[#999]"
      onClick={() => inputRef.current?.click()}
    >
      {logo ? (
        <div className="w-full relative">
          <label className="flex justify-start ms-2 text-lg gx-font-semibold">
            {label}
          </label>
          <button
            onClick={handleOnClose}
            className="p-1 bg-gray-200 rounded-full absolute top-0 right-2 z-10"
          >
            <img
              src={`${ICON_BASE_PATH}close-icon.svg`}
              width={14}
              height={14}
              alt="Close"
              className="cursor-pointer"
            />
          </button>

          {isImage ? (
            <div className="flex justify-center items-center">
              <img
                src={logo}
                alt="Preview"
                className="w-20 h-20 mt-1 rounded object-cover"
              />
            </div>
          ) : (
            <div className="flex justify-center items-center mt-1">
              <img
                src={`${ICON_BASE_PATH}fileIcon.svg`}
                alt="File"
                className="w-6 h-6 me-2"
              />
              <span className="text-sm text-[#2E3338] truncate max-w-[70%]">
                {fileName}
              </span>
            </div>
          )}
        </div>
      ) : (
        <>
          <img
            src={`${ICON_BASE_PATH}fileIcon.svg`}
            alt="Upload"
            className="w-6 h-6 mb-2"
          />
          <p className="text-sm gx-font-bold text-[#2E3338] mb-0!">
            {t("drag_and_drop")} {t("or")}{" "}
            <span className="text-[#1993D5]">{t("choose")}</span>{" "}
            {t("file_to_upload")}
          </p>
          <p className="text-xs text-[#999999] mt-1 mb-0!">
            {t("max_file_size_mb")}
          </p>

          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept={".pdf"}
            onChange={handleFileChange}
          />

          {touched && validationMessage && (
            <p className="text-sm text-red-500 mt-2">{validationMessage}</p>
          )}
        </>
      )}
    </div>
  );
};

export default LicenseUpload;
